import React, { useState } from 'react';
import { Box, IconButton, TextField, Typography } from '@mui/material';
import { i18n } from '../../i18n/i18n';
import { ReactComponent as CopyIcon } from '../../images/copy.svg';
import { ReactComponent as EditIcon } from '../../images/edit.svg';
import MuiDialogWrapper from '../../components/MuiDialogWrapper/MuiDialogWrapper';
import { Case } from '../../models/common-types';
import { COLORS } from '../../constants/constants';
import { copyToClipboard } from '../../utils/communication';

interface CaseInfoModalProps {
    close: () => void;
    data: { caseNode: Case; onCloseFunc: (caseName: string, caseDescription: string, messageKeys?: any) => void };
}

const CaseInfoDialog: React.FC<CaseInfoModalProps> = ({ close, data }) => {
    const [caseName, setCaseName] = useState(data.caseNode.name);
    const [isCaseNameFocused, setIsCaseNameFocused] = useState(false);
    const [caseDescription, setCaseDescription] = useState(data.caseNode.description);
    const [isCaseDescriptionFocused, setIsCaseDescriptionFocused] = useState(false);

    return (
        <MuiDialogWrapper
            styles={{ width: '388px' }}
            onClose={() => {
                close();
            }}
            divider={false}
            contentStyles={{ paddingTop: 0 }}
            content={
                <Box>
                    <Box
                        sx={{
                            'borderRadius': '8px',
                            'p': 1,
                            '&:hover': {
                                backgroundColor: COLORS.BRAND_LIGHTEST,
                            },
                            'display': 'inline-block',
                            '&:hover .show-name-pen': {
                                display: 'inline',
                                visibility: 'visible',
                            },
                        }}
                        onClick={() => setIsCaseNameFocused(true)}
                    >
                        {!isCaseNameFocused ? (
                            <Typography
                                variant="h5"
                                sx={{ fontWeight: 500, color: COLORS.MUTED_HEAVY, wordBreak: 'break-word' }}
                            >
                                {caseName}
                                <IconButton
                                    sx={{ padding: 0, ml: 2, visibility: 'hidden', display: 'none' }}
                                    className="show-name-pen"
                                    size="small"
                                >
                                    <EditIcon />
                                </IconButton>
                            </Typography>
                        ) : (
                            <TextField
                                autoFocus
                                value={caseName}
                                InputProps={{
                                    disableUnderline: true,
                                    style: { fontSize: '24px', width: '323px', lineHeight: '32px' },
                                }}
                                onChange={(e) => setCaseName(e.target.value)}
                                onBlur={() => {
                                    setIsCaseNameFocused(false);
                                    data.onCloseFunc(caseName, caseDescription);
                                }}
                                variant="standard"
                                fullWidth
                                multiline
                            />
                        )}
                    </Box>

                    <Typography variant="body1" sx={{ mt: 1, mb: 2, pl: 1, color: COLORS.MUTED_DARK, fontWeight: 500 }}>
                        {i18n.translate('Case ID')} {data.caseNode.id}
                        <IconButton onClick={() => copyToClipboard(`${data.caseNode.id}`)} color="inherit">
                            <CopyIcon fill={COLORS.MUTED_DARK} />
                        </IconButton>
                    </Typography>

                    <Box sx={{ fontSize: '16px' }} mb={1} pl={1}>
                        <Typography variant="inherit" sx={{ fontWeight: 500, color: COLORS.MUTED_HEAVY }}>
                            {i18n.translate('Case description')}
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            'borderRadius': '8px',
                            'p': 1,
                            '&:hover': {
                                backgroundColor: COLORS.BRAND_LIGHTEST,
                            },
                            '&:hover .show-description-pen': {
                                visibility: 'visible',
                            },
                            'display': 'inline-block',
                            'width': !isCaseDescriptionFocused ? 'auto' : '100%',
                        }}
                        onClick={() => setIsCaseDescriptionFocused(true)}
                    >
                        {!isCaseDescriptionFocused ? (
                            <Typography
                                variant="body1"
                                mb={1}
                                sx={{ color: COLORS.MUTED_HEAVY, wordBreak: 'break-word', width: '100%' }}
                            >
                                {caseDescription || i18n.translate('No desciption was provided to the current case')}
                                <IconButton
                                    sx={{ padding: 0, ml: 1, visibility: 'hidden' }}
                                    className="show-description-pen"
                                    size="small"
                                >
                                    <EditIcon />
                                </IconButton>
                            </Typography>
                        ) : (
                            <TextField
                                autoFocus
                                value={caseDescription}
                                InputProps={{
                                    disableUnderline: true,
                                    style: { fontSize: '14px', marginLeft: 1, lineHeight: '24px' },
                                }}
                                onChange={(e) => setCaseDescription(e.target.value)}
                                onFocus={(e) => e.target.select()}
                                onBlur={() => {
                                    setIsCaseDescriptionFocused(false);
                                    data.onCloseFunc(caseName, caseDescription, {
                                        success: 'CASE_DESCRIPTION_UPDATE_SUCCESS',
                                        error: 'CASE_DESCRIPTION_UPDATE_ERROR',
                                    });
                                }}
                                variant="standard"
                                multiline
                                fullWidth
                            />
                        )}
                    </Box>
                </Box>
            }
        />
    );
};

export default CaseInfoDialog;
